import {
  useState,
  useRef,
  createContext,
  isValidElement,
  Children,
  cloneElement,
} from "react";
import {
  useFloating,
  useClick,
  useInteractions,
  useListNavigation,
  useDismiss,
  useRole,
} from "@floating-ui/react";

import { noop } from "~/utils/helpers";

import "./Menu.css";

import type { ReactNode, MutableRefObject, HTMLProps } from "react";
import type { ContextData } from "@floating-ui/react";
import type { MenuItemProps } from "./MenuItem";

export interface MenuChangeEvent {
  value: string | null;
}

export interface MenuProps {
  children?: ReactNode;
  value?: string;
  onChange?: (event: MenuChangeEvent) => void;
}

export interface MenuContextValue {
  activeIndex: number | null;
  dataRef: ContextData;
  listRef: MutableRefObject<
    Array<HTMLButtonElement | HTMLAnchorElement | HTMLDivElement | null>
  >;
  selectedIndex: number;
  getItemProps: (userProps?: HTMLProps<HTMLElement>) => any;
  onChange: (event: MenuChangeEvent) => void;
  setActiveIndex: (index: number | null) => void;
  setSelectedIndex: (index: number) => void;
}

export const MenuContext = createContext({} as MenuContextValue);

export function Menu({ children, value, onChange }: MenuProps) {
  const listRef = useRef<
    Array<HTMLButtonElement | HTMLAnchorElement | HTMLDivElement | null>
  >([]);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [activeDescendant, setActiveDescendant] = useState<string>();
  const [selectedIndex, setSelectedIndex] = useState(
    Math.max(
      0,
      listRef.current.findIndex((el) => el?.id === value),
    ),
  );

  const { context } = useFloating({ open: true });

  const handleNavigate = (index: number | null) => {
    setActiveIndex(index ?? null);

    if (index != null) {
      setActiveDescendant(listRef.current[index]?.id);
    }
  };

  const { getFloatingProps, getItemProps } = useInteractions([
    useClick(context),
    useRole(context, { role: "menu" }),
    useDismiss(context),
    useListNavigation(context, {
      activeIndex,
      focusItemOnHover: false,
      listRef,
      onNavigate: handleNavigate,
    }),
  ]);

  const renderChildren = () => {
    return Children.map(
      children,
      (child, index) =>
        isValidElement(child) &&
        cloneElement(child, { index } as Partial<MenuItemProps>),
    );
  };

  return (
    <MenuContext.Provider
      value={{
        selectedIndex,
        setSelectedIndex,
        activeIndex,
        setActiveIndex,
        listRef,
        onChange: onChange ?? noop,
        getItemProps,
        dataRef: context.dataRef,
      }}
    >
      <div
        {...getFloatingProps({
          className: "Menu",
          "aria-activedescendant": activeDescendant,
          "aria-labelledby": undefined,
        })}
      >
        {renderChildren()}
      </div>
    </MenuContext.Provider>
  );
}
